import { useState } from "react";
import { EVENT_TYPES, EVENT_PRIORITY, buildSprints, getDaysInMonth, getFirstDayOfWeek, dateKey } from "./constants";

// ─── ReleaseEventDot ──────────────────────────────────────────────────────────
function ReleaseEventDot({ type, size = 14 }) {
  const t = EVENT_TYPES[type];
  if (!t) return null;
  if (type === 'sprint_end')
    return <span style={{ fontSize: size - 1, color: t.color, lineHeight: 1, display: 'inline-block' }}>⬡</span>;
  if (type === 'planned_release')
    return <span style={{ fontSize: size + 1, color: t.color, lineHeight: 1, display: 'inline-block' }}>★</span>;
  if (type === 'indian_holiday' || type === 'us_ca_holiday' || type === 'marketing')
    return <span style={{ fontSize: size, color: t.color, lineHeight: 1, display: 'inline-block' }}>{t.icon}</span>;
  if (type === 'hot_patch')
    return <span style={{ fontSize: size, color: t.color, lineHeight: 1, display: 'inline-block' }}>⚠</span>;
  return (
    <span style={{ display: 'inline-block', width: size - 3, height: size - 3, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
  );
}

// ─── ReleaseDayModal ──────────────────────────────────────────────────────────
function ReleaseDayModal({ dateStr, events, notes, codeFreezes, onSave, onClose }) {
  const [dayEvents, setDayEvents] = useState([...events]);
  const [note, setNote]           = useState(notes.find(n => n.date === dateStr)?.text || '');
  const [isCF, setIsCF]           = useState(codeFreezes.some(cf => dateStr >= cf.start && dateStr <= cf.end));

  const toggleEvent = (type) => {
    setDayEvents(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'var(--overlay)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:200,
    }}>
      <div style={{
        background:'var(--bg-card)', borderRadius:10, padding:24, width:360,
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)', border:'1px solid var(--border)',
      }}>
        <div style={{ fontSize:14, fontWeight:800, color:'var(--text-primary)', marginBottom:16 }}>
          {new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
          {Object.entries(EVENT_TYPES).map(([key, t]) => {
            const active = dayEvents.includes(key);
            return (
              <div key={key} onClick={() => toggleEvent(key)} style={{
                display:'flex', alignItems:'center', gap:10, padding:'7px 10px',
                borderRadius:6, cursor:'pointer',
                background: active ? t.bg : 'var(--bg-subtle)',
                border:`1.5px solid ${active ? t.color : 'var(--border)'}`,
                transition:'all 0.12s',
              }}>
                <ReleaseEventDot type={key} size={16} />
                <span style={{ fontSize:12, fontWeight:600, color: active ? t.color : 'var(--text-secondary)' }}>{t.label}</span>
                {active && <span style={{ marginLeft:'auto', fontSize:10, fontWeight:800, color:t.color }}>✓</span>}
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom:12 }}>
          <label htmlFor="rc-day-note" style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>Note</label>
          <textarea id="rc-day-note" value={note} onChange={e => setNote(e.target.value)}
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid var(--border)', borderRadius:5,
              padding:'8px 10px', fontSize:12, fontFamily:'inherit', resize:'vertical',
              minHeight:56, color:'var(--text-primary)', background:'var(--bg-input)' }}
            placeholder="e.g. QA taking longer for React Native upgrades" />
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
          <div onClick={() => setIsCF(v => !v)} style={{
            display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'7px 10px',
            borderRadius:6, border:`1.5px solid ${isCF ? '#3B82F6' : 'var(--border)'}`,
            background: isCF ? '#EFF6FF22' : 'var(--bg-subtle)', flex:1,
          }}>
            <span style={{ fontSize:12, color: isCF ? '#3B82F6' : 'var(--text-muted)' }}>◻ Code Freeze</span>
            {isCF && <span style={{ marginLeft:'auto', fontSize:10, fontWeight:800, color:'#3B82F6' }}>✓</span>}
          </div>
        </div>

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => onSave({ date: dateStr, events: dayEvents, note, isCF })}
            style={{ flex:1, padding:'8px 0', background:'var(--btn-primary)',
              color:'var(--btn-primary-txt)', border:'none', borderRadius:6, fontSize:12, fontWeight:700,
              cursor:'pointer', fontFamily:'inherit' }}>Save</button>
          <button onClick={onClose}
            style={{ padding:'8px 14px', background:'var(--btn-ghost-bg)', color:'var(--btn-ghost-txt)',
              border:'1px solid var(--btn-ghost-border)', borderRadius:6, fontSize:12, fontWeight:600,
              cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── ReleaseCalendarView ──────────────────────────────────────────────────────
export default function ReleaseCalendarView({ schedule, setSchedule, canEdit, sprintYear, roadmapName }) {
  const [editDate, setEditDate] = useState(null);
  const year = sprintYear || new Date().getFullYear();

  const safeSchedule = {
    events:      schedule?.events      || [],
    codeFreezes: schedule?.codeFreezes || [],
    notes:       schedule?.notes       || [],
  };

  const getDateEvents = (dk) => safeSchedule.events.filter(e => e.date === dk).map(e => e.type);
  const getDateNote   = (dk) => safeSchedule.notes.find(n => n.date === dk);
  const isCodeFreeze  = (dk) => safeSchedule.codeFreezes.some(cf => dk >= cf.start && dk <= cf.end);

  const handleSave = ({ date, events: evTypes, note, isCF }) => {
    setSchedule(prev => {
      const safe = { events: prev?.events || [], codeFreezes: prev?.codeFreezes || [], notes: prev?.notes || [] };
      const otherEvents = safe.events.filter(e => e.date !== date);
      const newEvents   = evTypes.map(type => ({ id: `${date}-${type}`, date, type }));
      const otherNotes  = safe.notes.filter(n => n.date !== date);
      const newNotes    = note.trim() ? [...otherNotes, { id: `note-${date}`, date, text: note.trim() }] : otherNotes;
      let newCF = safe.codeFreezes.filter(cf => !(date >= cf.start && date <= cf.end));
      if (isCF) {
        newCF = [...newCF, { start: date, end: date }];
      }
      return { events: [...otherEvents, ...newEvents], codeFreezes: newCF, notes: newNotes };
    });
    setEditDate(null);
  };

  const today      = new Date();
  const currentYear = today.getFullYear();
  const horizonSprints = buildSprints(year);
  const horizonEnd = horizonSprints[horizonSprints.length - 1].start;
  const cutoffMonth = year === currentYear ? Math.max(0, today.getMonth() - 1) : 0;
  const calendarMonths = [];
  for (let cursor = new Date(year, cutoffMonth, 1); cursor <= horizonEnd; cursor.setMonth(cursor.getMonth() + 1)) {
    calendarMonths.push({ year: cursor.getFullYear(), month: cursor.getMonth() });
  }
  const DOW         = ['S','M','T','W','T','F','S'];

  const title = `${roadmapName || 'Roadmap'} Release Schedule ${year}–${horizonEnd.getFullYear()}`;

  return (
    <div style={{ flex:1, overflowY:'auto', background:'var(--bg-subtle)' }}>
      {/* Header */}
      <div style={{ padding:'16px 24px 8px', borderBottom:'1px solid var(--border)', background:'var(--bg-header)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:16, fontWeight:800, color:'var(--text-primary)' }}>{title}</div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
            {canEdit ? "Click any date to add markers." : "Log in to edit release schedule."}
          </div>
        </div>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          {Object.entries(EVENT_TYPES).map(([key, t]) => (
            <span key={key} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--text-secondary)', whiteSpace:'nowrap' }}>
              <ReleaseEventDot type={key} size={12} />{t.label}
            </span>
          ))}
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#3B82F6', whiteSpace:'nowrap' }}>
            <span style={{ display:'inline-block', width:10, height:10, border:'2px solid #3B82F6', borderRadius:2 }} />Code Freeze
          </span>
        </div>
      </div>

      {/* Notes panel */}
      {(() => {
        const visibleNotes = safeSchedule.notes.filter(n => {
          const noteYear  = new Date(n.date + 'T12:00:00').getFullYear();
          const noteMonth = new Date(n.date + 'T12:00:00').getMonth();
          return calendarMonths.some(m => m.year === noteYear && m.month === noteMonth);
        });
        return visibleNotes.length > 0 && (
          <div style={{ display:'flex', gap:8, padding:'8px 24px', background:'#FFFBEB', borderBottom:'1px solid #FCD34D', flexWrap:'wrap' }}>
            <span style={{ fontSize:10, fontWeight:800, color:'#B45309', textTransform:'uppercase', letterSpacing:'0.06em', marginRight:4 }}>Notes</span>
            {visibleNotes.map(n => (
              <span key={n.id} style={{ fontSize:11, color:'#92400E', background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius:4, padding:'2px 8px' }}>
                <span style={{ fontWeight:700 }}>{new Date(n.date + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })}:</span> {n.text}
              </span>
            ))}
          </div>
        );
      })()}

      {/* Calendar grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, minmax(0,1fr))', gap:10, padding:14 }}>
        {calendarMonths.map(({ year: monthYear, month }) => {
          const daysInMonth = getDaysInMonth(monthYear, month);
          const firstDow    = getFirstDayOfWeek(monthYear, month);
          const monthName   = new Date(monthYear, month, 1).toLocaleString('default', { month:'long' });
          const cells = [];
          for (let i = 0; i < firstDow; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);

          return (
            <div key={`${monthYear}-${month}`} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
              <div style={{ background:'var(--text-primary)', color:'var(--bg-root)', textAlign:'center', padding:'5px 0', fontSize:11, fontWeight:800, letterSpacing:'0.04em' }}>
                {monthName} {monthYear}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', background:'var(--bg-subtle)' }}>
                {DOW.map((d,i) => (
                  <div key={i} style={{ textAlign:'center', fontSize:9, fontWeight:700, color:'var(--text-muted)', padding:'3px 0' }}>{d}</div>
                ))}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)' }}>
                {cells.map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} />;
                  const dk      = dateKey(monthYear, month, day);
                  const allEvts = getDateEvents(dk);
                  const dayEvts = [...allEvts].sort((a,b) => EVENT_PRIORITY.indexOf(a) - EVENT_PRIORITY.indexOf(b)).slice(0,2);
                  const hasMore = allEvts.length > 2;
                  const dayNote = getDateNote(dk);
                  const cf      = isCodeFreeze(dk);
                  const isToday = dk === new Date().toISOString().slice(0,10);
                  const dow     = (firstDow + day - 1) % 7;
                  const isWeekend = dow === 0 || dow === 6;

                  return (
                    <div key={day} onClick={canEdit ? () => setEditDate(dk) : undefined}
                      style={{
                        minHeight: 38, padding:'2px 2px 1px',
                        background: cf ? '#3B82F611' : isWeekend ? 'var(--bg-subtle)' : 'var(--bg-card)',
                        border: cf ? '0.5px solid #3B82F644' : `0.5px solid var(--border)`,
                        cursor: canEdit ? 'pointer' : 'default', position:'relative',
                        outline: isToday ? '2px solid #3B82F6' : 'none', outlineOffset: -1,
                        transition:'background 0.1s',
                      }}
                      onMouseEnter={e => { if (canEdit) e.currentTarget.style.background='var(--bg-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = cf ? '#3B82F611' : isWeekend ? 'var(--bg-subtle)' : 'var(--bg-card)'; }}
                    >
                      <div style={{
                        fontSize:10, fontWeight: isToday ? 800 : 500,
                        color: isToday ? '#fff' : isWeekend ? 'var(--text-muted)' : 'var(--text-secondary)',
                        width:16, height:16, borderRadius:'50%', display:'flex',
                        alignItems:'center', justifyContent:'center', marginBottom:1,
                        background: isToday ? '#3B82F6' : 'transparent', lineHeight:1,
                      }}>{day}</div>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:1, alignItems:'center' }}>
                        {dayEvts.map(type => <ReleaseEventDot key={type} type={type} size={14} />)}
                        {hasMore && <span style={{ fontSize:9, fontWeight:700, color:'var(--text-muted)', lineHeight:1 }}>+{allEvts.length - 2}</span>}
                      </div>
                      {dayNote && (
                        <div style={{ position:'absolute', bottom:1, right:2, width:4, height:4, borderRadius:'50%', background:'#F59E0B' }} title={dayNote.text} />
                      )}
                      {cf && safeSchedule.codeFreezes.some(c => c.start === dk) && (
                        <div style={{ position:'absolute', bottom:0, left:0, right:0, fontSize:9, fontWeight:800, color:'#3B82F6', textAlign:'center', background:'#3B82F622', letterSpacing:'0.02em', lineHeight:1.2 }}>FREEZE</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {editDate && (
        <ReleaseDayModal
          dateStr={editDate}
          events={getDateEvents(editDate)}
          notes={safeSchedule.notes}
          codeFreezes={safeSchedule.codeFreezes}
          onSave={handleSave}
          onClose={() => setEditDate(null)}
        />
      )}
    </div>
  );
}
